import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import useClientPlants from "../../../../hooks/useClientPlants";
import ModuleIconBadge from "../../../../components/ModuleIconBadge";
import StatCard from "../../../../components/StatCard";
import EmptyState from "../../../../components/EmptyState";
import AppPageHeader from "../../../../components/AppPageHeader";
import { countPlantWbs } from "../plant/plantUtils";

const clientLabel = (plant) => plant.client?.name || plant.client?.prefix || "";

const matchesSearch = (plant, query) => {
  if (!query) return true;
  const haystack = [plant.name, plant.description, clientLabel(plant)]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
};

const ProjectCard = ({ plant, onOpen }) => {
  const teamSize = Array.isArray(plant.people) ? plant.people.length : 0;

  return (
    <Card sx={{ height: "100%" }}>
      <CardActionArea
        onClick={onOpen}
        sx={{
          height: "100%",
          p: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1.25,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <ModuleIconBadge moduleKey="plant" size="md" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {plant.name || "Untitled"}
            </Typography>
            {clientLabel(plant) && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {clientLabel(plant)}
              </Typography>
            )}
          </Box>
        </Stack>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            flex: 1,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            minHeight: "2.6em",
          }}
        >
          {plant.description || "No description"}
        </Typography>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${countPlantWbs(plant)} WBS`} />
          <Chip
            size="small"
            icon={<GroupsOutlinedIcon />}
            label={teamSize}
            variant="outlined"
          />
          {plant.aimsSent && (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<CheckCircleIcon />}
              label="In AIMS"
            />
          )}
        </Stack>
      </CardActionArea>
    </Card>
  );
};

/**
 * The "Project" nav item's destination: an overview of every project the
 * user can see, each opening its project hub (/plant/:id/open). The sidebar
 * tree beside it is still the way into a project's WBS and documents - this
 * page is the starting point before that pick happens.
 *
 * Replaces the old PlantsTable grid as the "Project" landing page - that
 * component (src/pages/client/Project/plant/index.jsx) is left in place,
 * just no longer routed here, in case it's needed again.
 */
export default function ProjectLanding() {
  const navigate = useNavigate();
  const { plants, loading } = useClientPlants();
  const [search, setSearch] = useState("");

  const query = search.trim().toLowerCase();
  const visiblePlants = useMemo(
    () => plants.filter((plant) => matchesSearch(plant, query)),
    [plants, query],
  );

  const totalWbs = plants.reduce((sum, plant) => sum + countPlantWbs(plant), 0);
  const inAimsCount = plants.filter((plant) => plant.aimsSent).length;
  const clientCount = new Set(plants.map(clientLabel).filter(Boolean)).size;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <AppPageHeader
        moduleKey="projects"
        title="Projects"
        subtitle="Open a project here, or drill into its WBS and documents from the tree on the left."
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            moduleKey="plant"
            label="Projects"
            value={plants.length}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            moduleKey="estimation"
            label="WBS"
            value={totalWbs}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            moduleKey="aims"
            label="Sent to AIMS"
            value={inAimsCount}
            loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            moduleKey="client-data"
            label="Clients"
            value={clientCount}
            loading={loading}
          />
        </Grid>
      </Grid>

      <TextField
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search by project, client or description"
        size="small"
        sx={{ mb: 2, width: { xs: "100%", sm: 380 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="disabled" />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }, (_, index) => (
            <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
              <Skeleton variant="rounded" height={168} />
            </Grid>
          ))}
        </Grid>
      ) : visiblePlants.length === 0 ? (
        <EmptyState
          moduleKey="projects"
          title={query ? "No matching projects" : "No projects yet"}
          description={
            query
              ? "Try a different name or client."
              : "Projects you create, or that are shared with your company, show up here."
          }
        />
      ) : (
        <Grid container spacing={2}>
          {visiblePlants.map((plant) => (
            <Grid key={plant.id} item xs={12} sm={6} md={4} lg={3}>
              <ProjectCard
                plant={plant}
                onOpen={() => navigate(`/plant/${plant.id}/open`)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
